<script lang="ts">
	import { format } from 'svelte-i18n';
	import { onMount } from 'svelte';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { readDeviceSettings, readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import SettingGuide from '../../SettingGuide.svelte';
	import { Alert, Container, Loader, Text, TokenV2, ToggleV2 } from '@Shelly/shelly-design-system';
	import { devices_config } from '@devices/index';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		get_mode,
	} from 'src/helpers/interface_helpers';
	import { deviceHasSettingTitle } from 'src/helpers/settings/device_has_setting_title';
	import {
		deviceFirmwareAtLeast,
		EMETER_CT_TYPE_MIN_VER,
		getEmeterSetConfigMethod,
		isEmeterEm1,
	} from 'src/helpers/settings/emeter_settings_helpers';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import { genCharArray } from 'src/helpers/utilities';
	import { gen2Request } from 'src/services/gen_2_http_reqs';

	export let device: Device;

	const settings = readDeviceSettings(device.id);
	const configKey = get_gen_2_component_channel_structure(device);
	const component = get_gen_2_component_from_category(device) || 'em';
	const method = getEmeterSetConfigMethod(device);
	const isEm1 = isEmeterEm1(device);
	const showReverse = deviceHasSettingTitle(device, 'reverse_measurement_direction');
	const showCtType =
		deviceHasSettingTitle(device, 'change_ct_type') &&
		deviceFirmwareAtLeast(settings, EMETER_CT_TYPE_MIN_VER);
	const showSequence = !isEm1 && deviceHasSettingTitle(device, 'monitor_phase_sequence');
	const mode = get_mode(device, readDeviceStatus(device.id), settings);
	const phases = genCharArray(devices_config[device.type]?.modes?.[mode]?.phases);
	const emSettings = settings?.[configKey];

	let reverseChecked = !!emSettings?.reverse;
	let savedReverseChecked = reverseChecked;
	let reverseByPhase: Record<string, boolean> = {};
	let savedReverseByPhase: Record<string, boolean> = {};
	let ctType: string = emSettings?.ct_type || '';
	let savedCtType = ctType;
	let supportedCtTypes: string[] = [];
	let ctTypesLoading = false;
	let sequenceChecked = !!emSettings?.monitor_phase_sequence;
	let savedSequenceChecked = sequenceChecked;
	let loading = false;

	if (!isEm1) {
		for (const phase of phases) {
			reverseByPhase[phase] = !!emSettings?.reverse?.[phase];
			savedReverseByPhase[phase] = reverseByPhase[phase];
		}
	}

	$: reverseDirty = isEm1
		? reverseChecked !== savedReverseChecked
		: phases.some((phase) => !!reverseByPhase[phase] !== !!savedReverseByPhase[phase]);
	$: ctDirty = showCtType && ctType !== savedCtType;
	$: sequenceDirty = showSequence && sequenceChecked !== savedSequenceChecked;
	$: isDirty = reverseDirty || ctDirty || sequenceDirty;
	$: saveDisabled = loading || ctTypesLoading || !isDirty;

	const phaseLabel = (phase: string): string =>
		$format(`label_phase_${phase}`, { default: `Phase ${phase}` });

	const loadCtTypes = async () => {
		if (!showCtType) return;
		ctTypesLoading = true;
		const res = await gen2Request(device, `${component}.GetCTTypes`, {
			id: device.channel,
		});
		supportedCtTypes = res.isok && res?.data?.supported ? res.data.supported : ['50A'];
		if (!supportedCtTypes.includes(ctType)) {
			ctType = supportedCtTypes[0] || '';
			savedCtType = ctType;
		}
		ctTypesLoading = false;
	};

	onMount(() => {
		void loadCtTypes();
	});

	const save = async () => {
		if (saveDisabled) return;

		const config: Record<string, unknown> = {};
		if (reverseDirty) {
			config.reverse = isEm1 ? reverseChecked : { ...reverseByPhase };
		}
		if (ctDirty) config.ct_type = ctType;
		if (sequenceDirty) config.monitor_phase_sequence = sequenceChecked;

		loading = true;
		const res: SettingRequest = await gen2Request(device, method, {
			id: device.channel ?? 0,
			config,
		});
		if (res?.isok) {
			if (reverseDirty) {
				if (isEm1) {
					savedReverseChecked = reverseChecked;
				} else {
					savedReverseByPhase = { ...reverseByPhase };
				}
			}
			if (ctDirty) savedCtType = ctType;
			if (sequenceDirty) savedSequenceChecked = sequenceChecked;
		}
		onSettingsResponse(res, device);
		loading = false;
	};

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		setStickyBottomAction({
			onClick: save,
			loading,
			disabled: saveDisabled,
			icon: 'fas fa-save',
		});
	}
</script>

<div class="emeter-measurement">
	<SettingGuide
		text={$format('settings_guide_emeter_measurement', {
			default:
				'Match the meter to how the CTs were installed, including reverse, type, and phase order.',
		})}
	/>

	{#if showReverse}
		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_heading_emeter_reverse', {
					default: 'Reverse measurement',
				})}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('settings_guide_reverse_measurement', {
					default:
						'Flip the measured power direction if the sensor was installed backwards. Use this when import and export look swapped, without rewiring.',
				})}
			</Text>
			{#if isEm1}
				<ToggleV2
					size="m"
					bind:checked={reverseChecked}
					disabled={loading}
					id={`toggle-emeter-reverse-${device.channel ?? 0}`}
					aria-label={$format('settings_toggle_reverse_measurement', {
						default: 'Reverse measurement',
					})}
				>
					{$format('settings_toggle_reverse_measurement', {
						default: 'Reverse measurement',
					})}
				</ToggleV2>
			{:else}
				{#each phases as phase (phase)}
					<ToggleV2
						size="m"
						class="mb-s"
						checked={!!reverseByPhase[phase]}
						disabled={loading}
						id={`toggle-emeter-reverse-${phase}`}
						aria-label={phaseLabel(phase)}
						on:click={() => {
							reverseByPhase = {
								...reverseByPhase,
								[phase]: !reverseByPhase[phase],
							};
						}}
					>
						{phaseLabel(phase)}
					</ToggleV2>
				{/each}
			{/if}
		</Container>
	{/if}

	{#if showCtType}
		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_heading_emeter_ct_type', {
					default: 'CT type',
				})}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('settings_guide_change_ct_type', {
					default:
						'Tell the device which current transformer (CT) is connected so power readings match the clamp. Choose the rating printed on the CT.',
				})}
			</Text>
			{#if ctTypesLoading}
				<Loader />
			{:else}
				{#if supportedCtTypes.length === 1}
					<Alert
						class="mb-s"
						type="info"
						heading={$format('alert_device_supports_one_ct', {
							default: 'The device only supports one type of CT.',
						})}
					/>
				{/if}
				<div class="d-flex flex-wrap">
					{#each supportedCtTypes as type (type)}
						<TokenV2
							id={`token-emeter-ct-${type}`}
							selected={ctType === type}
							size="m"
							title={type}
							class="mr-s mb-s"
							aria-label={type}
							on:click={() => {
								ctType = type;
							}}
						/>
					{/each}
				</div>
			{/if}
		</Container>
	{/if}

	{#if showSequence}
		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_heading_emeter_phase_sequence', {
					default: 'Phase sequence',
				})}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('monitor_phase_sequence_description', {
					default:
						'Set this to indicate if current transformers are placed incorrectly in a three phase system.',
				})}
			</Text>
			<ToggleV2
				size="m"
				bind:checked={sequenceChecked}
				disabled={loading}
				id="toggle-emeter-phase-sequence"
				aria-label={$format('label_enable', { default: 'Enable' })}
			>
				{$format('label_enable', { default: 'Enable' })}
			</ToggleV2>
		</Container>
	{/if}
</div>

<style>
	.emeter-measurement {
		display: flex;
		flex-direction: column;
		gap: 12px;
		width: 100%;
	}
</style>
